/**
 * Brittany's Corner - Family Legacy Intelligence Center
 * PHASE C2.5: Fully Interactive UI - Premium NWGSV Design
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import './brittany.css';

// Types
interface DashboardData {
  goals_count: number;
  timeline_count: number;
  notes_count: number;
  projects_count: number;
  education_avg_progress: number;
  recent_activity: any[];
}

interface Goal {
  id: string;
  title: string;
  description: string;
  target_date?: string;
  status: string;
  progress: number;
  created_at: string;
}

interface TimelineEntry {
  id: string;
  date: string;
  title: string;
  description: string;
  category: string;
  created_at: string;
}

interface Note {
  id: string;
  title: string;
  content: string;
  category: string;
  created_at: string;
  updated_at: string;
}

interface EducationCourse {
  id: string;
  course_name: string;
  category: string;
  progress: number;
  status: string;
  started_at?: string;
  completed_at?: string;
}

interface CreatorProject {
  id: string;
  title: string;
  description: string;
  status: string;
  category: string;
  created_at: string;
  updated_at: string;
}

export default function BrittanyCorner({ onBack }: { onBack: () => void }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'timeline' | 'education' | 'creator' | 'notes' | 'goals' | 'ai'>('dashboard');
  
  // Data states
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [education, setEducation] = useState<EducationCourse[]>([]);
  const [projects, setProjects] = useState<CreatorProject[]>([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // AI states
  const [aiMessage, setAiMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'timeline') loadTimeline();
    else if (activeTab === 'notes') loadNotes();
    else if (activeTab === 'education') loadEducation();
    else if (activeTab === 'creator') loadProjects();
    else if (activeTab === 'goals') loadGoals();
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/brittany/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGoals = async () => {
    try {
      const response = await apiCall('/api/brittany/goals');
      if (response.ok) {
        const data = await response.json();
        setGoals(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadTimeline = async () => {
    try {
      const response = await apiCall('/api/brittany/timeline');
      if (response.ok) {
        const data = await response.json();
        setTimeline(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadNotes = async () => {
    try {
      const response = await apiCall('/api/brittany/notes');
      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadEducation = async () => {
    try {
      const response = await apiCall('/api/brittany/education');
      if (response.ok) {
        const data = await response.json();
        setEducation(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await apiCall('/api/brittany/creator');
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('/api/brittany/goals', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        setShowForm(false);
        await loadGoals();
        form.reset();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create goal');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateGoalProgress = async (goalId: string, newProgress: number) => {
    try {
      const formData = new FormData();
      formData.append('progress', newProgress.toString());
      if (newProgress >= 100) formData.append('status', 'Completed');
      
      const response = await fetch(`/api/brittany/goals/${goalId}`, {
        method: 'PUT',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        await loadGoals();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAIQuery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiMessage.trim()) return;
    
    setAiLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('message', aiMessage);
      
      const response = await fetch('/api/brittany/ai', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.response);
        setAiMessage('');
      } else {
        let detail = 'AI request failed. Please try again.';
        try {
          const data = await response.json();
          detail = data.detail || detail;
        } catch {
          // Non-JSON error body (e.g. proxy/plain-text 500) — keep friendly message
        }
        setError(detail);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const filteredTimeline = timeline.filter(entry =>
    (!searchQuery || entry.title.toLowerCase().includes(searchQuery.toLowerCase()) || entry.description.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!selectedCategory || entry.category === selectedCategory)
  );

  const filteredNotes = notes.filter(note =>
    (!searchQuery || note.title.toLowerCase().includes(searchQuery.toLowerCase()) || note.content.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!selectedCategory || note.category === selectedCategory)
  );

  return (
    <div className="module-root" style={styles.container}>
      {/* Header */}
      <div className="module-header" style={styles.header}>
        <div>
          <h1 style={styles.title}>BRITTANY'S CORNER</h1>
          <p style={styles.subtitle}>Family Legacy • Education • Creativity • Growth</p>
        </div>
        <button style={styles.backButton} onClick={onBack}>
          ← RETURN TO CUBE
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Tab Navigation */}
      <div className="module-tabbar" style={styles.tabBar}>
        {['dashboard', 'goals', 'timeline', 'education', 'creator', 'notes', 'ai'].map(tab => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab(tab as any)}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="module-content" style={styles.content}>
        {/* DASHBOARD */}
        {activeTab === 'dashboard' && dashboard && (
          <div style={styles.dashboardGrid}>
            <div style={styles.card} onClick={() => setActiveTab('goals')}>
              <div style={styles.cardIcon}>🎯</div>
              <div style={styles.cardNumber}>{dashboard.goals_count}</div>
              <div style={styles.cardLabel}>Active Goals</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('education')}>
              <div style={styles.cardIcon}>📚</div>
              <div style={styles.cardNumber}>{dashboard.education_avg_progress}%</div>
              <div style={styles.cardLabel}>Education Progress</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('creator')}>
              <div style={styles.cardIcon}>✨</div>
              <div style={styles.cardNumber}>{dashboard.projects_count}</div>
              <div style={styles.cardLabel}>Creator Projects</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('timeline')}>
              <div style={styles.cardIcon}>📖</div>
              <div style={styles.cardNumber}>{dashboard.timeline_count}</div>
              <div style={styles.cardLabel}>Timeline Entries</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('notes')}>
              <div style={styles.cardIcon}>🏛️</div>
              <div style={styles.cardNumber}>{dashboard.notes_count}</div>
              <div style={styles.cardLabel}>Family Notes</div>
            </div>
            <div style={styles.card} onClick={() => setActiveTab('ai')}>
              <div style={styles.cardIcon}>🤖</div>
              <div style={styles.cardNumber}>AI</div>
              <div style={styles.cardLabel}>Assistant Ready</div>
            </div>
          </div>
        )}

        {/* GOALS */}
        {activeTab === 'goals' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Personal Goals</h2>
              <button style={styles.createButton} onClick={() => setShowForm(!showForm)}>
                {showForm ? 'CANCEL' : '+ NEW GOAL'}
              </button>
            </div>

            {showForm && (
              <form onSubmit={handleCreateGoal} style={styles.form}>
                <input name="title" placeholder="Goal Title" required style={styles.input} />
                <textarea name="description" placeholder="Description" required style={styles.textarea} rows={3} />
                <input name="target_date" type="date" style={styles.input} />
                <button type="submit" style={styles.submitButton}>CREATE GOAL</button>
              </form>
            )}

            <div style={styles.goalsList}>
              {goals.map(goal => (
                <div key={goal.id} style={styles.goalCard}>
                  <div style={styles.goalHeader}>
                    <div style={styles.goalTitle}>{goal.title}</div>
                    <div style={{...styles.goalStatus, color: goal.status === 'Completed' ? '#00C853' : '#FFD700'}}>
                      {goal.status}
                    </div>
                  </div>
                  <div style={styles.goalDesc}>{goal.description}</div>
                  {goal.target_date && (
                    <div style={styles.goalDate}>Target: {new Date(goal.target_date).toLocaleDateString()}</div>
                  )}
                  <div style={styles.progressBar}>
                    <div style={{...styles.progressFill, width: `${goal.progress}%`}} />
                  </div>
                  <div style={styles.progressLabel}>{goal.progress}% Complete</div>
                  {user?.role === 'SUPER_ADMIN' || user?.role === 'FAMILY' ? (
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={goal.progress}
                      onChange={(e) => handleUpdateGoalProgress(goal.id, parseInt(e.target.value))}
                      style={styles.slider}
                    />
                  ) : null}
                </div>
              ))}
              {goals.length === 0 && (
                <div style={styles.empty}>No goals yet. Create your first goal to get started!</div>
              )}
            </div>
          </div>
        )}

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Family Legacy Timeline</h2>
            </div>

            <div className="module-filterbar" style={styles.filterBar}>
              <input
                type="text"
                placeholder="Search timeline..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={styles.select}
              >
                <option value="">All Categories</option>
                <option value="Family">Family</option>
                <option value="Education">Education</option>
                <option value="Business">Business</option>
                <option value="Achievement">Achievement</option>
                <option value="Legacy">Legacy</option>
              </select>
            </div>

            <div style={styles.timelineList}>
              {filteredTimeline.map(entry => (
                <div key={entry.id} style={styles.timelineItem}>
                  <div style={styles.timelineDate}>{new Date(entry.date).toLocaleDateString()}</div>
                  <div style={styles.timelineContent}>
                    <div style={styles.timelineTitle}>{entry.title}</div>
                    <div style={styles.timelineDesc}>{entry.description}</div>
                    <span style={styles.categoryBadge}>{entry.category}</span>
                  </div>
                </div>
              ))}
              {filteredTimeline.length === 0 && (
                <div style={styles.empty}>No timeline entries found</div>
              )}
            </div>
          </div>
        )}

        {/* EDUCATION CENTER */}
        {activeTab === 'education' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Education Center</h2>
            </div>

            <div style={styles.educationGrid}>
              {education.map(course => (
                <div key={course.id} style={styles.courseCard}>
                  <div style={styles.courseHeader}>
                    <div style={styles.courseName}>{course.course_name}</div>
                    <div style={styles.courseCategory}>{course.category}</div>
                  </div>
                  <div style={styles.progressCircle}>
                    <svg width="100" height="100">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,215,0,0.2)" strokeWidth="8" />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#FFD700"
                        strokeWidth="8"
                        strokeDasharray={`${2 * Math.PI * 40}`}
                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - course.progress / 100)}`}
                        transform="rotate(-90 50 50)"
                      />
                      <text x="50" y="55" textAnchor="middle" fill="#FFD700" fontSize="20" fontWeight="bold">
                        {course.progress}%
                      </text>
                    </svg>
                  </div>
                  <div style={styles.courseStatus}>{course.status}</div>
                  {course.started_at && (
                    <div style={styles.courseDate}>Started: {new Date(course.started_at).toLocaleDateString()}</div>
                  )}
                </div>
              ))}
              {education.length === 0 && (
                <div style={styles.empty}>No courses enrolled yet</div>
              )}
            </div>
          </div>
        )}

        {/* CREATOR STUDIO */}
        {activeTab === 'creator' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Creator Studio</h2>
            </div>

            <div style={styles.projectBoard}>
              {['Idea', 'Draft', 'In Progress', 'Published'].map(status => (
                <div key={status} style={styles.projectColumn}>
                  <div style={styles.columnHeader}>{status}</div>
                  <div style={styles.columnContent}>
                    {projects.filter(p => p.status === status).map(project => (
                      <div key={project.id} style={styles.projectCard}>
                        <div style={styles.projectTitle}>{project.title}</div>
                        <div style={styles.projectDesc}>{project.description.substring(0, 80)}...</div>
                        <div style={styles.projectCategory}>{project.category}</div>
                        <div style={styles.projectDate}>
                          {new Date(project.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAMILY NOTES */}
        {activeTab === 'notes' && (
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Family Notes</h2>
            </div>

            <div className="module-filterbar" style={styles.filterBar}>
              <input
                type="text"
                placeholder="Search notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={styles.select}
              >
                <option value="">All Categories</option>
                <option value="Personal">Personal</option>
                <option value="Family">Family</option>
                <option value="Business">Business</option>
                <option value="Legacy">Legacy</option>
                <option value="Ideas">Ideas</option>
              </select>
            </div>

            <div style={styles.notesList}>
              {filteredNotes.map(note => (
                <div key={note.id} style={styles.noteCard}>
                  <div style={styles.noteHeader}>
                    <div style={styles.noteTitle}>{note.title}</div>
                    <span style={styles.categoryBadge}>{note.category}</span>
                  </div>
                  <div style={styles.noteContent}>{note.content}</div>
                  <div style={styles.noteDate}>
                    {new Date(note.updated_at).toLocaleString()}
                  </div>
                </div>
              ))}
              {filteredNotes.length === 0 && (
                <div style={styles.empty}>No notes found</div>
              )}
            </div>
          </div>
        )}

        {/* AI ASSISTANT */}
        {activeTab === 'ai' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Brittany's Corner AI Assistant</h2>
            
            <div style={styles.aiSuggestions}>
              <div style={styles.suggestionLabel}>Try asking:</div>
              {[
                'Help me achieve my goals',
                'Suggest a business idea',
                'Build a learning plan',
                'Create a legacy project',
                'Analyze my progress'
              ].map(prompt => (
                <button
                  key={prompt}
                  style={styles.suggestionButton}
                  onClick={() => setAiMessage(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>

            {aiResponse && (
              <div style={styles.aiResponse}>
                <div style={styles.aiLabel}>AI Response:</div>
                <div style={styles.aiText}>{aiResponse}</div>
              </div>
            )}

            <form onSubmit={handleAIQuery} style={styles.aiForm}>
              <textarea
                value={aiMessage}
                onChange={(e) => setAiMessage(e.target.value)}
                placeholder="Ask Brittany's Corner AI anything..."
                style={styles.aiInput}
                rows={4}
              />
              <button
                type="submit"
                disabled={aiLoading || !aiMessage.trim()}
                style={{
                  ...styles.aiButton,
                  opacity: aiLoading || !aiMessage.trim() ? 0.5 : 1
                }}
              >
                {aiLoading ? 'THINKING...' : 'ASK AI'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    background: 'black',
    color: '#FFD700',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '30px 40px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    opacity: 0.7,
  },
  backButton: {
    padding: '12px 24px',
    background: 'rgba(255, 215, 0, 0.1)',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  error: {
    padding: '15px 40px',
    background: 'rgba(255, 0, 0, 0.1)',
    border: '1px solid #ff4444',
    color: '#ff4444',
  },
  tabBar: {
    display: 'flex',
    gap: '10px',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
    overflowX: 'auto',
  },
  tab: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  tabActive: {
    background: 'rgba(255, 215, 0, 0.2)',
    borderColor: '#FFD700',
  },
  content: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  card: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '30px',
    textAlign: 'center',
    transition: 'all 0.3s',
    cursor: 'pointer',
  },
  cardIcon: {
    fontSize: '48px',
    marginBottom: '15px',
  },
  cardNumber: {
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  cardLabel: {
    fontSize: '14px',
    opacity: 0.7,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  section: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  createButton: {
    padding: '12px 24px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  form: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
  },
  textarea: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  select: {
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
  },
  submitButton: {
    padding: '14px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  goalsList: {
    display: 'grid',
    gap: '20px',
  },
  goalCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  goalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  goalTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
  },
  goalStatus: {
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  goalDesc: {
    fontSize: '14px',
    opacity: 0.8,
    marginBottom: '10px',
  },
  goalDate: {
    fontSize: '12px',
    opacity: 0.6,
    marginBottom: '15px',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    background: 'rgba(255, 215, 0, 0.2)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '10px',
  },
  progressFill: {
    height: '100%',
    background: '#FFD700',
    transition: 'width 0.3s ease',
  },
  progressLabel: {
    fontSize: '12px',
    textAlign: 'center',
    marginBottom: '10px',
  },
  slider: {
    width: '100%',
    cursor: 'pointer',
  },
  filterBar: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '12px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
  },
  timelineList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  timelineItem: {
    display: 'flex',
    gap: '20px',
    padding: '20px',
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
  },
  timelineDate: {
    fontSize: '14px',
    fontWeight: 'bold',
    minWidth: '120px',
  },
  timelineContent: {
    flex: 1,
  },
  timelineTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  timelineDesc: {
    fontSize: '14px',
    opacity: 0.8,
    marginBottom: '10px',
  },
  categoryBadge: {
    fontSize: '12px',
    padding: '4px 12px',
    background: 'rgba(255, 215, 0, 0.2)',
    borderRadius: '4px',
  },
  educationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px',
  },
  courseCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
  },
  courseHeader: {
    marginBottom: '20px',
  },
  courseName: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  courseCategory: {
    fontSize: '12px',
    opacity: 0.7,
  },
  progressCircle: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '15px',
  },
  courseStatus: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  courseDate: {
    fontSize: '12px',
    opacity: 0.6,
  },
  projectBoard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  projectColumn: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  columnHeader: {
    padding: '15px',
    background: 'rgba(255, 215, 0, 0.1)',
    borderBottom: '1px solid rgba(255, 215, 0, 0.3)',
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  columnContent: {
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  projectCard: {
    padding: '15px',
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.2)',
    borderRadius: '4px',
  },
  projectTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  projectDesc: {
    fontSize: '12px',
    opacity: 0.8,
    marginBottom: '8px',
  },
  projectCategory: {
    fontSize: '11px',
    padding: '3px 8px',
    background: 'rgba(255, 215, 0, 0.2)',
    borderRadius: '3px',
    display: 'inline-block',
    marginBottom: '5px',
  },
  projectDate: {
    fontSize: '11px',
    opacity: 0.6,
  },
  notesList: {
    display: 'grid',
    gap: '15px',
  },
  noteCard: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
  },
  noteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  noteTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  noteContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    opacity: 0.9,
    marginBottom: '15px',
  },
  noteDate: {
    fontSize: '12px',
    opacity: 0.6,
  },
  aiSuggestions: {
    marginBottom: '30px',
  },
  suggestionLabel: {
    fontSize: '14px',
    marginBottom: '15px',
    opacity: 0.7,
  },
  suggestionButton: {
    display: 'block',
    width: '100%',
    padding: '12px',
    marginBottom: '10px',
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    color: '#FFD700',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'left',
    transition: 'all 0.2s',
  },
  aiResponse: {
    background: 'rgba(255, 215, 0, 0.05)',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '30px',
  },
  aiLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  aiText: {
    fontSize: '14px',
    lineHeight: '1.6',
    opacity: 0.9,
    whiteSpace: 'pre-wrap',
  },
  aiForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  aiInput: {
    padding: '15px',
    background: 'black',
    border: '1px solid #FFD700',
    color: '#FFD700',
    borderRadius: '4px',
    outline: 'none',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  aiButton: {
    padding: '14px',
    background: '#FFD700',
    color: 'black',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    opacity: 0.7,
    fontSize: '16px',
  },
};
